import { IonActionSheet, IonCheckbox, IonIcon, IonInput, IonLabel, IonSpinner } from '@ionic/react'
import { ENodeVehicle } from '@vae/server/src/interfaces/eNode/eNodeVehicle.interface'
import { car, chevronDown, locationOutline } from 'ionicons/icons'
import React, { useContext, useEffect, useRef, useState } from 'react'
import Autocomplete from 'react-google-autocomplete'
import { useTranslation } from 'react-i18next'
import { logEvent } from '../../../../analytics/utils/firebaseUtils'
import ModeSwitch from '../../../../components/ModeSwitch'
import Button from '../../../../components/actions/Button'
import useUser from '../../../../contexts/hooks/useUser'
import { ObjectContext } from '../../../../contexts/objectContext/ObjectContext'
import EnodeInitHeader from '../../../../modals/eNodeInit/components/ENodeInitHeader'
import { trpc } from '../../../../modules/trpc'
import PageWrapper from '../../../../wrappers/pageWrapper/PageWrapper'

interface AddChargingStationProps {
	vehicles: ENodeVehicle[]
	goForward: () => void
	position?: number
	vehiclesLoading: boolean
	backPath?: string
}

const AddChargingStation: React.FC<AddChargingStationProps> = ({
	vehicles,
	goForward,
	vehiclesLoading,
	backPath,
}) => {
	const utils = trpc.useContext()

	const { user } = useUser()

	const object = useContext(ObjectContext)

	const [actionSheetOpen, setActionSheetOpen] = useState(false)
	const [locationName, setLocationName] = useState('Koti')
	const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(null)
	const [selectedVehicle, setSelectedVehicle] = useState<ENodeVehicle | null>(null)
	const [vehicleOnTop, setVehicleOnTop] = useState<ENodeVehicle>()
	const [selectedAddress, setSelectedAddress] = useState('')
	const [includedInObject, setIncludedInObject] = useState(true)
	const [locationMethod, setLocationMethod] = useState<'vehicle' | 'address'>('vehicle')
	const [chargingStationExists, setChargingStationExists] = useState(false)
	const [justCreated, setJustCreated] = useState(false)

	const autoInputRef = useRef<any>()

	const { t } = useTranslation('pages_enodeInit')

	const { refetch: refetchChargingStations } = trpc.eNode.charging.stations.get.useQuery(
		{ hashedPid: user!.hashedPid },
		{
			enabled: Boolean(user?.hashedPid),
			onSuccess: data => {
				setChargingStationExists(data.length > 0)
			},
		}
	)

	useEffect(() => {
		if (justCreated && chargingStationExists) {
			setJustCreated(false)
			goForward()
		}
	}, [justCreated, chargingStationExists, goForward])

	trpc.eNode.vehicles.getSingle.useQuery(
		{
			hashedPid: user!.hashedPid,
			vehicleId: vehicleOnTop?.id ?? '',
			fieldToUpdate: 'location',
		},
		{
			enabled: Boolean((vehicleOnTop?.id ?? '').length > 1),
			onSuccess(data) {
				setChargingStationExists(true)
				if (data?.location && data.location.latitude && data.location.longitude)
					setCoordinates({ lat: data.location.latitude, lng: data.location.longitude })
			},
			refetchInterval(data) {
				if (!data?.location.lastUpdated) return 1000
				else return false
			},
		}
	)

	useEffect(() => {
		if (vehicles[0]) {
			setSelectedVehicle(vehicles[0])
			setCoordinates({
				lat: vehicles[0].location.latitude ?? 0,
				lng: vehicles[0].location.longitude ?? 0,
			})

			if (!vehicleOnTop) setVehicleOnTop(vehicles[0])
		}
	}, [vehicles])

	const { mutate: createChargingStation, isLoading } =
		trpc.eNode.charging.stations.create.useMutation({
			onSuccess: () => {
				utils.eNode.charging.stations.invalidate()
			},
			onSettled: () => {
				refetchChargingStations()
				setJustCreated(true)
			},
		})

	const handleSave = async () => {
		if (!coordinates || !object.id) return
		logEvent('click-save-ev-charging-location')

		createChargingStation({
			hashedPid: user!.hashedPid,
			latitude: coordinates?.lat,
			longitude: coordinates?.lng,
			name: locationName,
			objectId: includedInObject ? object.id : undefined,
		})
	}

	useEffect(() => {
		if (locationMethod === 'vehicle' && vehicleOnTop?.location) {
			setSelectedVehicle(vehicleOnTop)
			setCoordinates({
				lat: vehicleOnTop.location.latitude ?? 0,
				lng: vehicleOnTop.location.longitude ?? 0,
			})
		} else {
			setSelectedAddress('-')
			setSelectedVehicle(null)
			setCoordinates(null)
		}
	}, [locationMethod, vehicleOnTop])

	if (vehiclesLoading)
		return (
			<div className='content' id='add-charging-station'>
				<IonSpinner />
			</div>
		)

	const modeOptions = [
		{ mode: 'vehicle', label: t('addChargingStation.autonSijainti') },
		{ mode: 'address', label: t('addChargingStation.syotaOsoite') },
	]

	return (
		<PageWrapper page='addChargingLocation' appBarOptions={{ backPath: backPath }}>
			<div className='flex flex-row gap-4'>
				<EnodeInitHeader position={1} total={5} text={t('addChargingStation.otsikko')} />
				<div id='add-charging-station' className='w-full h-full'>
					<div className='h-[38px] flex items-center mb-2'>
						<span className='text-xl tracking-wide font-semibold dark:text-brand-secondary-100-dark'>
							{t('addChargingStation.otsikko')}
						</span>
					</div>

					<div className='inputs'>
						<div className='input-container'>
							<span className='dark:text-white'>{t('addChargingStation.nimi')}</span>
							<IonInput
								value={locationName}
								onIonChange={e => setLocationName(e.detail.value ?? '')}
								className='dark:bg-surface-dark dark:text-white border-brand-primary-100-light dark:border-brand-primary-100-dark'
							/>
						</div>

						<div className='input-container location-show'>
							<IonIcon
								className='text-brand-primary-100-light dark:text-brand-primary-100-dark'
								icon={locationOutline}
							/>
							<IonLabel class='dark:text-white'>
								{t('addChargingStation.sijainti')} <br />
								<b>
									{locationMethod === 'vehicle' && selectedVehicle
										? `${selectedVehicle.information.brand} ${selectedVehicle.information.model} (nykyinen olinpaikka)`
										: (selectedAddress ?? '')}
								</b>
							</IonLabel>
						</div>

						<div className='input-container checkbox'>
							<IonCheckbox
								checked={includedInObject}
								onIonChange={e => setIncludedInObject(e.detail.checked)}
								className='dark:[--border-color:#3cb49f] dark:[--border-color-checked:#3cb49f] dark:[--checkbox-background-checked:#3cb49f] dark:[--checkbox-background:#144448] [--border-color:#52ae32] [--border-color-checked:#52ae32] [--checkbox-background-checked:#52ae32] [--checkbox-background:#cae3cc]'
							/>
							<IonLabel class='dark:text-white'>
								{t('addChargingStation.nakyyKulutuksessa')} <br />
								<b>
									{object.streetAddress} {object.houseNumber}
								</b>
							</IonLabel>
						</div>

						<div className='location-switch rounded-xl overflow-hidden border bg-background-200-light border-[#d7d7d7] dark:border-brand-secondary-200-dark'>
							<div className='dark:bg-background-200-dark'>
								<div className='p-4 pb-0'>
									<ModeSwitch
										options={modeOptions}
										currentMode={locationMethod}
										onModeChange={mode => setLocationMethod(mode as 'vehicle' | 'address')}
									/>
								</div>
								<div className='details p-4 pt-0 dark:bg-background-200-dark'>
									{locationMethod === 'vehicle' && (
										<>
											<div
												className='car bg-white dark:bg-brand-secondary-200-dark border dark:border-brand-primary-200-dark'
												onClick={() => {
													setActionSheetOpen(vehicles.length > 1)
													setSelectedVehicle(vehicleOnTop ?? vehicles[0])
													setSelectedAddress('')
													if (autoInputRef.current) autoInputRef.current.value = ''
													setCoordinates({
														lat: vehicleOnTop?.location.latitude ?? 0,
														lng: vehicleOnTop?.location.longitude ?? 0,
													})
												}}
											>
												<IonIcon
													className='auto-icon rounded-full dark:bg-brand-secondary-100-dark dark:text-brand-primary-100-dark'
													icon={car}
												/>
												<span className='text-text-dark dark:text-white font-semibold tracking-wide'>
													{vehicleOnTop?.information.brand} {vehicleOnTop?.information.model}
												</span>
												<div className='grow'></div>
												{vehicles.length > 1 && (
													<IonIcon className='chevron dark:text-[#c8c8c8]' icon={chevronDown} />
												)}
											</div>

											<div className='current-location text-text-dark dark:text-white'>
												<div className='row'>
													{t('addChargingStation.pituuspiiri')}
													<br />
													<b> {coordinates?.lat ?? <IonSpinner />}</b>
												</div>
												<div className='row'>
													{t('addChargingStation.leveyspiiri')}
													<br />
													<b>{coordinates?.lng ?? <IonSpinner />}</b>
												</div>
											</div>
										</>
									)}

									{locationMethod === 'address' && (
										<div className='address-search'>
											<Autocomplete
												apiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY!}
												ref={autoInputRef}
												options={{
													types: [
														'street_number',
														'street_address',
														'route',
														'premise',
														'subpremise',
													],
													componentRestrictions: { country: 'fi' },
												}}
												onPlaceSelected={place => {
													const lat = place.geometry?.location?.lat() ?? 0
													const lng = place.geometry?.location?.lng() ?? 0

													setCoordinates({ lat, lng })
													setSelectedVehicle(null)
													setSelectedAddress(place.formatted_address ?? '')
												}}
												placeholder={t('addChargingStation.tarkkaOsoite')}
												className='dark:!bg-surface-dark dark:!text-white !border-brand-primary-100-light dark:!border-brand-primary-100-dark'
											/>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
					<Button
						disabled={coordinates === null}
						onClick={handleSave}
						className='w-full dark:![--background:#3cb49f] ![--background:#52ae32]'
					>
						{t('addChargingStation.tallenna')}
						{isLoading && <IonSpinner />}
					</Button>
				</div>
			</div>

			<IonActionSheet
				isOpen={actionSheetOpen}
				onDidDismiss={() => setActionSheetOpen(false)}
				header={t('addChargingStation.valitseAuto')}
				buttons={vehicles.map(vehicle => ({
					text: `${vehicle.information.brand} ${vehicle.information.model}`,
					icon: car,
					handler: () => {
						setSelectedVehicle(vehicle)
						setVehicleOnTop(vehicle)
						setSelectedAddress('')
						setCoordinates({
							lat: vehicle.location.latitude ?? 0,
							lng: vehicle.location.longitude ?? 0,
						})
					},
				}))}
			/>
		</PageWrapper>
	)
}

export default AddChargingStation
